"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import {
  GoogleContactForm,
  type GoogleContactFormProps,
} from "@modes0/ui-components/client";
import { createServiceContactFormContent } from "@/content/business-details/business-content";

const GoogleReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});

type ServiceContactFormContent = ReturnType<
  typeof createServiceContactFormContent
>;

type ServiceContactFormProps = {
  content: ServiceContactFormContent;
  className?: string;
};

type WindowWithGoogleRecaptcha = Window & {
  grecaptcha?: {
    getResponse?: () => string;
    reset?: () => void;
  };
};

function isLocalBrowserHost() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function getGoogleRecaptchaResponse() {
  if (typeof window === "undefined") return null;
  const token = (
    window as WindowWithGoogleRecaptcha
  ).grecaptcha?.getResponse?.();
  return token || null;
}

export default function ServiceContactForm({
  className,
  content,
}: ServiceContactFormProps) {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [formResetNonce, setFormResetNonce] = useState(0);
  const isLocalHost = isLocalBrowserHost();
  const captchaSiteKey =
    isLocalHost && content.captcha.devSiteKey
      ? content.captcha.devSiteKey
      : content.captcha.siteKey;
  const formConfig = content.form as GoogleContactFormProps;
  const formApiUrl =
    isLocalHost && content.localApiUrl
      ? content.localApiUrl
      : formConfig.apiUrl;

  const resetCaptcha = () => {
    setCaptchaToken(null);
    (window as WindowWithGoogleRecaptcha).grecaptcha?.reset?.();
    setCaptchaKey((key) => key + 1);
  };

  const getCaptchaToken = () => captchaToken || getGoogleRecaptchaResponse();

  const handleSuccess: GoogleContactFormProps["onSuccess"] = (
    response,
    payload,
  ) => {
    formConfig.onSuccess?.(response, payload);
    setFormResetNonce((nonce) => nonce + 1);
  };

  return (
    <GoogleContactForm
      {...formConfig}
      apiUrl={formApiUrl}
      className={className || "ozy-contact-form ozy-service-contact-form"}
      initialValues={{
        ...(formConfig.initialValues ?? {}),
        __resetNonce: formResetNonce,
      }}
      onSuccess={handleSuccess}
      captchaElement={
        captchaSiteKey ? (
          <div className="ozy-recaptcha-field ozy-service-recaptcha-field">
            <div className="ozy-recaptcha-copy">
              <span className="ozy-recaptcha-label">Security check</span>
              <span className="ozy-recaptcha-note">
                Tick the box before sending your enquiry.
              </span>
            </div>
            <GoogleReCAPTCHA
              key={captchaKey}
              sitekey={captchaSiteKey}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
        ) : undefined
      }
      getCaptchaToken={captchaSiteKey ? getCaptchaToken : undefined}
      resetCaptcha={captchaSiteKey ? resetCaptcha : undefined}
    />
  );
}
